import * as ScrollArea from '@radix-ui/react-scroll-area';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from '@/components/Icons/Check';
import { ChevronRightFilled } from '@/components/Icons/ChevronRightFilled';
import { Modal } from '@/components/Modal';
import { useCloseOnBreakpointChange } from '@/hooks/useCloseOnBreakpointChange';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useSwipeToClose } from '@/hooks/useSwipeToClose';
import { ModalSize, ModalVariant } from '@/types/components';
import type { StudyYearResponse } from '@/types/user';
import { tv } from '@/utils/tv';

const switcherCard = tv({
  base: 'flex items-center justify-between rounded-xl p-3 shadow-card transition-colors',
  variants: {
    isOpen: {
      true: 'bg-info-surface',
      false: 'bg-card',
    },
  },
});

const arrow = tv({
  base: 'flex size-4 shrink-0 cursor-pointer items-center justify-center text-primary transition-opacity',
  variants: {
    isDisabled: { true: 'cursor-default opacity-40' },
    isHidden: { true: 'invisible' },
  },
});

const yearOption = tv({
  base: 'flex w-full cursor-pointer items-center gap-2 rounded-xl py-3 pl-3 pr-2 text-left text-body-14 transition-colors',
  variants: {
    isSelected: {
      true: 'bg-info-surface text-subhead-14 text-primary',
      false: 'text-foreground hover:bg-hover',
    },
  },
});

interface Props {
  studyYears: StudyYearResponse[];
  selectedIndex: number;
  onSelect: (index: number) => void;
}

export const StudyYearSwitcher: React.FC<Props> = ({
  studyYears,
  selectedIndex,
  onSelect,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const { sheetRef, swipeProps } = useSwipeToClose(() => setIsOpen(false));

  const selectedYear = studyYears[selectedIndex];
  const hasMultipleYears = studyYears.length > 1;
  const hasPreviousYear = selectedIndex < studyYears.length - 1;
  const hasNextYear = selectedIndex > 0;

  const selectYear = (index: number) => {
    setIsOpen(false);
    onSelect(index);
  };

  useCloseOnBreakpointChange(isOpen, () => setIsOpen(false));

  useEffect(() => {
    if (!isOpen || isMobile) {
      return;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) {
        return;
      }

      setIsOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, isMobile]);

  if (!selectedYear) {
    return null;
  }

  return (
    <div ref={containerRef} className="relative w-full">
      <div className={switcherCard({ isOpen })}>
        <button
          type="button"
          disabled={!hasPreviousYear}
          aria-label={t('profile.previousStudyYear')}
          onClick={() => selectYear(selectedIndex + 1)}
          className={arrow({
            isDisabled: !hasPreviousYear,
            isHidden: !hasMultipleYears,
            className: 'rotate-180',
          })}
        >
          <ChevronRightFilled className="size-4" />
        </button>

        <button
          type="button"
          disabled={!hasMultipleYears}
          onClick={() => setIsOpen((prev) => !prev)}
          className="flex items-center justify-center gap-1 px-1 text-subhead-14 text-primary enabled:cursor-pointer"
        >
          {t('profile.studyYearName', { name: selectedYear.name })}
          {selectedYear.notificationCount > 0 && (
            <span className="size-2 shrink-0 rounded-full bg-accent" />
          )}
        </button>

        <button
          type="button"
          disabled={!hasNextYear}
          aria-label={t('profile.nextStudyYear')}
          onClick={() => selectYear(selectedIndex - 1)}
          className={arrow({
            isDisabled: !hasNextYear,
            isHidden: !hasMultipleYears,
          })}
        >
          <ChevronRightFilled className="size-4" />
        </button>
      </div>

      {isOpen && !isMobile && (
        <div className="absolute inset-x-0 top-full z-50 mt-3">
          <ScrollArea.Root
            type="scroll"
            className="max-h-[252px] overflow-hidden rounded-xl bg-card shadow-modal"
          >
            <ScrollArea.Viewport className="max-h-[252px] w-full">
              <div className="flex flex-col gap-1 p-2">
                {studyYears.map((studyYear, index) => (
                  <button
                    key={studyYear.name}
                    type="button"
                    onClick={() => selectYear(index)}
                    className={yearOption({
                      isSelected: index === selectedIndex,
                    })}
                  >
                    <span className="flex-1 truncate">
                      {t('profile.studyYearName', { name: studyYear.name })}
                    </span>
                    {studyYear.notificationCount > 0 && (
                      <span className="size-2 shrink-0 rounded-full bg-accent" />
                    )}
                    {index === selectedIndex && (
                      <Check className="size-4 shrink-0" />
                    )}
                  </button>
                ))}
              </div>
            </ScrollArea.Viewport>
            <ScrollArea.Scrollbar
              orientation="vertical"
              forceMount
              className="z-10 flex w-[7px] touch-none pr-[1px] opacity-70 transition-opacity duration-300 ease-out select-none data-[state=hidden]:opacity-0"
            >
              <ScrollArea.Thumb className="relative flex-1 rounded-full bg-[var(--scrollbar-thumb)]" />
            </ScrollArea.Scrollbar>
          </ScrollArea.Root>
        </div>
      )}

      {isMobile && (
        <Modal
          isOpen={isOpen}
          label={t('profile.selectStudyYear')}
          variant={ModalVariant.BottomSheet}
          size={ModalSize.Small}
          className="max-md:h-[420px]"
          contentRef={sheetRef}
          onClose={() => setIsOpen(false)}
        >
          <div
            {...swipeProps}
            className="flex shrink-0 cursor-grab flex-col touch-none active:cursor-grabbing"
          >
            <div className="flex justify-center pt-2 pb-1">
              <div className="h-1 w-10 rounded-full bg-drag-handle" />
            </div>
            <div className="flex items-center px-4 pt-3 pb-3">
              <h2 className="text-title-30 text-foreground">
                {t('profile.selectStudyYear')}
              </h2>
            </div>
          </div>

          <div className="flex flex-1 flex-col gap-2 overflow-y-auto overscroll-contain px-4 pb-[calc(24px+env(safe-area-inset-bottom))]">
            {studyYears.map((studyYear, index) => (
              <button
                key={studyYear.name}
                type="button"
                onClick={() => selectYear(index)}
                className="flex shrink-0 cursor-pointer items-center gap-3 rounded-2xl bg-card py-5 pr-3 pl-4 shadow-card"
              >
                <span className="flex-1 truncate text-left text-body-16 leading-5 text-foreground">
                  {t('profile.studyYearName', { name: studyYear.name })}
                </span>
                {studyYear.notificationCount > 0 && (
                  <span className="size-2 shrink-0 rounded-full bg-accent" />
                )}
                <span className="flex size-6 shrink-0 items-center justify-center">
                  {index === selectedIndex && (
                    <Check className="size-4 text-primary" />
                  )}
                  {index !== selectedIndex && (
                    <ChevronRightFilled className="size-4 text-muted-foreground" />
                  )}
                </span>
              </button>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
};
